class CouponPopup extends HTMLElement {
    constructor() {
      super();
  
      // Don't run on Shopify challenge page
      if (window.location.pathname === '/challenge') return;
  
      this.STORAGE_KEY = 'coupon_popup_last_closed';
      this.SESSION_KEY = 'coupon_popup_session_closed';
      this.EMAIL_SUBMITTED_KEY = 'coupon_popup_email_submitted';
  
      this.delay = parseInt(this.dataset.delay, 10) || 0;
      this.frequency = this.dataset.frequency || '12';
      this.testMode = this.dataset.testMode === 'true';
      this.hideCouponUntilEmail = this.dataset.hideCouponUntilEmail === 'true';
  
      // Check if returning from email submission
      this._checkEmailSubmissionReturn();
  
      this._bindEvents();
      this._initPopup();
    }

    _checkEmailSubmissionReturn() {
      // Check if we're returning from a successful email submission
      const urlParams = new URLSearchParams(window.location.search);
      const customerPosted = urlParams.get('customer_posted') === 'true';
      
      if (customerPosted && this.hideCouponUntilEmail) {
        // Mark email as submitted
        try {
          localStorage.setItem(this.EMAIL_SUBMITTED_KEY, 'true');
        } catch (_) {}
        
        // Open popup immediately (no delay) to show coupon
        this.emailJustSubmitted = true;
        
        // Clean up URL without reloading
        const cleanUrl = window.location.pathname + window.location.hash.replace('#contact_form', '');
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }

    _hasSubmittedEmail() {
      try {
        return localStorage.getItem(this.EMAIL_SUBMITTED_KEY) === 'true';
      } catch (_) {
        return false;
      }
    }
  
    /* -------------------------------------------
       Lifecycle — Shopify theme editor support
    ------------------------------------------- */
    connectedCallback() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this._onSectionSelect = (e) => this._filterEvent(e, () => this.open());
        this._onSectionDeselect = (e) => this._filterEvent(e, () => this.close());
        document.addEventListener('shopify:section:select', this._onSectionSelect);
        document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
      }
    }
  
    disconnectedCallback() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this._onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
      }
    }
  
    _filterEvent(event, callback) {
      if (event.detail && event.detail.sectionId && this.dataset.sectionId) {
        if (event.detail.sectionId === this.dataset.sectionId) {
          callback();
        }
      }
    }
  
    /* -------------------------------------------
       Initialization
    ------------------------------------------- */
    _bindEvents() {
      // Close buttons / overlay
      this.querySelectorAll('[data-coupon-popup-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });
  
      // Close on Escape
      this._onKeydown = (e) => {
        if (e.key === 'Escape') this.close();
      };
  
      // Copy coupon
      const copyBtn = this.querySelector('[data-copy-coupon]');
      if (copyBtn) {
        copyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this._copyCoupon(copyBtn);
        });
      }

      // Email form submission - show coupon after submit
      if (this.dataset.hideCouponUntilEmail === 'true') {
        this._bindEmailForm();
      }
    }

    _bindEmailForm() {
      const emailForm = this.querySelector('.coupon-popup__email-form');
      const couponBlock = this.querySelector('[data-coupon-hidden]');

      if (!emailForm || !couponBlock) return;

      emailForm.addEventListener('submit', () => {
        // Mark email as submitted before the page redirects
        try {
          localStorage.setItem(this.EMAIL_SUBMITTED_KEY, 'true');
        } catch (_) {}
        // Let the form submit naturally - Shopify will handle the redirect
      });
    }
  
    _initPopup() {
      // In Shopify editor, don't auto-open
      if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
  
      // If email was just submitted (returning from redirect), open immediately
      if (this.emailJustSubmitted) {
        this._showCouponHideEmail();
        this.open();
        return;
      }

      if (!this._shouldShow()) return;

      // If email was already submitted previously, show coupon directly
      if (this.hideCouponUntilEmail && this._hasSubmittedEmail()) {
        this._showCouponHideEmail();
      }
  
      setTimeout(() => {
        this.open();
      }, this.delay * 1000);
    }

    _showCouponHideEmail() {
      const couponBlock = this.querySelector('[data-coupon-hidden]');
      const emailBlock = this.querySelector('.coupon-popup__email');

      if (emailBlock) {
        emailBlock.style.display = 'none';
      }
      if (couponBlock) {
        couponBlock.style.display = '';
        couponBlock.removeAttribute('data-coupon-hidden');
      }
    }
  
    /* -------------------------------------------
       Frequency / visibility logic
    ------------------------------------------- */
    _shouldShow() {
      // Test mode: always show
      if (this.testMode) return true;
  
      // "always" — show every load
      if (this.frequency === 'always') return true;
  
      // "once_per_session" — check sessionStorage
      if (this.frequency === 'once_per_session') {
        try {
          if (sessionStorage.getItem(this.SESSION_KEY)) return false;
        } catch (_) {
          // sessionStorage not available
        }
        return true;
      }
  
      // Hours-based frequency — check localStorage
      try {
        const lastClosed = localStorage.getItem(this.STORAGE_KEY);
        if (!lastClosed) return true;
  
        const hoursSince = (Date.now() - parseInt(lastClosed, 10)) / (1000 * 60 * 60);
        const frequencyHours = parseFloat(this.frequency);
  
        return hoursSince >= frequencyHours;
      } catch (_) {
        // localStorage not available, show it
        return true;
      }
    }
  
    _recordClose() {
      if (this.testMode) return;
  
      if (this.frequency === 'once_per_session') {
        try {
          sessionStorage.setItem(this.SESSION_KEY, '1');
        } catch (_) {}
        return;
      }
  
      if (this.frequency !== 'always') {
        try {
          localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
        } catch (_) {}
      }
    }
  
    /* -------------------------------------------
       Open / Close
    ------------------------------------------- */
    open() {
      this.style.display = '';
      this.classList.add('is-visible');
      this.classList.remove('is-closing');
  
      // Trigger reflow so the animation plays
      void this.offsetHeight;
      this.classList.add('is-open');
  
      document.addEventListener('keydown', this._onKeydown);
      document.body.style.overflow = 'hidden';
    }
  
    close() {
      this.classList.remove('is-open');
      this.classList.add('is-closing');
  
      this._recordClose();
      document.removeEventListener('keydown', this._onKeydown);
  
      // Wait for the closing animation to complete
      const onEnd = () => {
        this.classList.remove('is-visible', 'is-closing');
        this.style.display = 'none';
        document.body.style.overflow = '';
        this.removeEventListener('animationend', onEnd);
      };
      this.addEventListener('animationend', onEnd);
  
      // Fallback in case animationend doesn't fire
      setTimeout(onEnd, 400);
    }
  
    /* -------------------------------------------
       Copy coupon to clipboard
    ------------------------------------------- */
    _copyCoupon(btn) {
      const codeEl = this.querySelector('[data-coupon-code]');
      if (!codeEl) return;
  
      const code = codeEl.textContent.trim();
  
      const writeToClipboard = (text) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          return navigator.clipboard.writeText(text);
        }
        // Fallback for older browsers
        return new Promise((resolve, reject) => {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
          document.body.appendChild(textarea);
          textarea.select();
          try {
            document.execCommand('copy');
            resolve();
          } catch (err) {
            reject(err);
          }
          document.body.removeChild(textarea);
        });
      };
  
      writeToClipboard(code)
        .then(() => {
          const labelEl = btn.querySelector('[data-copy-label]');
          const copiedEl = btn.querySelector('[data-copied-label]');
  
          if (labelEl) labelEl.style.display = 'none';
          if (copiedEl) copiedEl.style.display = '';
          btn.classList.add('is-copied');
        })
        .catch(() => {
          // Silently fail — the user can still manually select the code
        });
    }
  }
  
  customElements.define('coupon-popup', CouponPopup);